import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—'
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

export function formatDateTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '—'
  return new Date(dateStr).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

export function formatPaise(paise: number): string {
  const rupees = paise / 100
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(rupees)
}

export function getInitials(name: string): string {
  return name
    .split(' ')
    .slice(0, 2)
    .map((n) => n[0])
    .join('')
    .toUpperCase()
}

export function daysRemaining(dateStr: string): number {
  const target = new Date(dateStr)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const diff = target.getTime() - today.getTime()
  return Math.floor(diff / (1000 * 60 * 60 * 24))
}

export function timeAgo(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diff = now.getTime() - date.getTime()
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)
  if (days > 0) return `${days}d ago`
  if (hours > 0) return `${hours}h ago`
  if (minutes > 0) return `${minutes}m ago`
  return 'just now'
}

export function getExpiryColor(days: number): string {
  if (days < 0) return 'bg-gray-100 text-gray-800'
  if (days <= 14) return 'bg-red-100 text-red-800'
  if (days <= 30) return 'bg-amber-100 text-amber-800'
  if (days <= 60) return 'bg-yellow-100 text-yellow-800'
  return 'bg-green-100 text-green-800'
}

export function getExpiryLabel(days: number): string {
  if (days < 0) return 'Expired'
  if (days === 0) return 'Today'
  if (days === 1) return 'Tomorrow'
  return `${days}d`
}

export function getRecordTypeColor(recordType: string): string {
  switch (recordType) {
    case 'licence':
      return 'bg-blue-100 text-blue-800'
    case 'insurance':
      return 'bg-purple-100 text-purple-800'
    case 'puc':
      return 'bg-teal-100 text-teal-800'
    case 'fitness':
      return 'bg-green-100 text-green-800'
    case 'road_tax':
      return 'bg-orange-100 text-orange-800'
    case 'env_tax':
      return 'bg-lime-100 text-lime-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

export type SubscriptionStatusBucket = 'active' | 'trial' | 'expired'

// Maps the real subscription_status enum (trialing | active | past_due | suspended | cancelled,
// see db/init.sql) to the 3-bucket view the superadmin UI shows. past_due/suspended/cancelled
// all read as "expired" from an admin's glance at a list of agencies.
export function bucketSubscriptionStatus(status: string): SubscriptionStatusBucket {
  if (status === 'active') return 'active'
  if (status === 'trialing') return 'trial'
  return 'expired'
}

export function formatRecordType(recordType: string): string {
  switch (recordType) {
    case 'licence':
      return 'Licence'
    case 'insurance':
      return 'Insurance'
    case 'puc':
      return 'PUC'
    case 'fitness':
      return 'Fitness Certificate'
    case 'road_tax':
      return 'Road Tax'
    case 'env_tax':
      return 'Env Tax'
    default:
      return recordType
  }
}
